import type React from "react";
import type {PaginationGridRowModel} from "../BaseTableDataModel.ts";
import type {DeserializedGridCellParams} from "../MuiXColumnDefinitions.ts";
import {Warning} from "@mui/icons-material";
import {SubGroupPaginationCell} from "./SubGroupPaginationCell.tsx";
import type {BaseMuixTableController} from "../controllers/BaseMuixTableController.tsx";
import type {MuiXGroupedDataResponse} from "../MuixApiTypes.ts";
import {GridKeyboardArrowRight, GridExpandMoreIcon} from "@mui/x-data-grid-premium";
import {CircularProgress, IconButton, Stack, Tooltip} from "@mui/material";
import {plural} from "../../../utils/js-utils.ts";
import {observer} from "mobx-react-lite";

type Props =
    Omit<DeserializedGridCellParams<PaginationGridRowModel>, 'id' | 'colDef' | 'field' | 'tabIndex' | 'api' | 'cellMode' | 'hasFocus'>
    & {
        controller: BaseMuixTableController,
    };
export const GeneralGroupingCell: React.FC<Props> = observer(({controller, rowNode, row}) => {
    // Note that subGroupPagination row has one item in path more than it's parrent
    const paddingInlineStart = ((row.path?.length ?? 2) - 2) * 4;
    if (rowNode.type === 'leaf' && row.subGroupPagination)
        return <SubGroupPaginationCell row_id={row.id} controller={controller}
                                       paddingInlineStart={String(paddingInlineStart) + 'em'}/>;
    if (rowNode.type === "group" && !rowNode.isAutoGenerated) {
        const groupinCountText = row.path.length < controller.rowGroupingModel.length ?
            controller.tableColumns.find(c => c.field === controller.rowGroupingModel[row.path.length])?.headerName ?? '' :
            'Transaction';
        const groupingCount = (row as MuiXGroupedDataResponse<any>).count;
        const isExpanded = controller.expandedRowIds.has(row.id);
        const isLoading = controller.loadingRowIds.has(row.id);
        const isDisableInteraction = controller.disableInteractionRowIds.has(row.id);

        return <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            data-grouping-fit-content
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                width: 'max-content',
                paddingInlineStart: `${paddingInlineStart + 4}em`,
            }}
        >
            {isLoading ?
                <IconButton disabled><CircularProgress size={24}/></IconButton> :
                isDisableInteraction ?
                    <div className="grouping-toggle-replace-container">
                        <Tooltip title="Please refresh to see the latest changes">
                            <Warning color="primary"/>
                        </Tooltip>
                    </div> :
                    <IconButton
                        onClick={() => controller.setGroupExpansion(row.id, !isExpanded)}>
                        {isExpanded ?
                            <GridExpandMoreIcon/> :
                            <GridKeyboardArrowRight/>}
                    </IconButton>}
            {(row as MuiXGroupedDataResponse<any>).group}&thinsp;
            ({groupingCount} {plural(groupinCountText, groupingCount)})
        </Stack>;
    }
    return null;
});
